import { StorageInterface } from '../stores/childNavStores';
import { AdminLaunchRequestListDto, AdminLaunchRequestDto } from '../types/adminAssessment';

/**
 * AdminAssessmentStorage - Manages local storage of launch request lists
 *
 * Storage Structure:
 * - Key: admin_launch_requests_{accountId}
 * - Value: AdminLaunchRequestListDto containing array of AdminLaunchRequestDto
 *
 * Populated from login or token-refresh responses so that assessments are
 * available offline without an additional API call.
 */
export class AdminAssessmentStorage {
  private storage: StorageInterface;
  private readonly keyPrefix = 'admin_launch_requests_';

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private getStorageKey(accountId: string): string {
    return `${this.keyPrefix}${accountId}`;
  }

  // ── Write operations ───────────────────────────────────────────────────────

  /**
   * Replace the entire launch-request list for an account.
   * Called when a fresh list arrives from the login / refresh response.
   */
  async saveLaunchRequestList(
    accountId: string,
    list: AdminLaunchRequestListDto
  ): Promise<boolean> {
    try {
      const key = this.getStorageKey(accountId);
      await this.storage.setItem(key, JSON.stringify(list));
      console.log('💾 AdminAssessmentStorage: Saved launch request list:', {
        accountId,
        requestCount: list.adminRequests.length,
        storageKey: key,
      });
      return true;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Error saving launch request list:', error);
      return false;
    }
  }

  /**
   * Merge incoming requests with the existing list.
   * Deduplicates on launchqueueId; incoming entries win on conflict.
   */
  async mergeLaunchRequests(
    accountId: string,
    incoming: AdminLaunchRequestDto[]
  ): Promise<boolean> {
    try {
      const existing = await this.getLaunchRequestList(accountId);

      // Build a map keyed by launchqueueId so incoming entries overwrite stale ones
      const map = new Map<string, AdminLaunchRequestDto>();
      for (const req of existing.adminRequests) {
        map.set(req.launchqueueId, req);
      }
      for (const req of incoming) {
        map.set(req.launchqueueId, req);
      }

      const merged: AdminLaunchRequestListDto = {
        adminRequests: Array.from(map.values()),
      };

      return this.saveLaunchRequestList(accountId, merged);
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Error merging launch requests:', error);
      return false;
    }
  }

  /**
   * Update the status field of a single launch request in place.
   */
  async updateRequestStatus(
    accountId: string,
    launchqueueId: string,
    status: string
  ): Promise<boolean> {
    try {
      const list = await this.getLaunchRequestList(accountId);
      const updated = list.adminRequests.map(r =>
        r.launchqueueId === launchqueueId ? { ...r, status } : r
      );
      return this.saveLaunchRequestList(accountId, { adminRequests: updated });
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Error updating request status:', error);
      return false;
    }
  }

  /**
   * Remove all launch requests for an account (e.g. on sign-out).
   */
  async clearLaunchRequestList(accountId: string): Promise<boolean> {
    try {
      const key = this.getStorageKey(accountId);
      await this.storage.removeItem(key);
      console.log('🗑️ AdminAssessmentStorage: Cleared launch request list for account:', accountId);
      return true;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Error clearing launch request list:', error);
      return false;
    }
  }

  // ── Read operations ────────────────────────────────────────────────────────

  /**
   * Get the full launch-request list for an account.
   * Returns an empty list if nothing is stored.
   */
  async getLaunchRequestList(accountId: string): Promise<AdminLaunchRequestListDto> {
    try {
      const key = this.getStorageKey(accountId);
      const raw = await this.storage.getItem(key);

      if (raw) {
        const list: AdminLaunchRequestListDto = JSON.parse(raw);
        console.log('📖 AdminAssessmentStorage: Retrieved launch request list:', {
          accountId,
          requestCount: list.adminRequests.length,
        });
        return list;
      }

      console.log('📖 AdminAssessmentStorage: No launch requests found for account:', accountId);
      return { adminRequests: [] };
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Error retrieving launch request list:', error);
      return { adminRequests: [] };
    }
  }

  /**
   * Get all launch requests for a specific profile within an account.
   */
  async getRequestsForProfile(
    accountId: string,
    profileId: string
  ): Promise<AdminLaunchRequestDto[]> {
    const list = await this.getLaunchRequestList(accountId);
    return list.adminRequests.filter(r => r.profileId === profileId);
  }

  /**
   * Get a single launch request by its launchqueueId.
   */
  async getRequestById(
    accountId: string,
    launchqueueId: string
  ): Promise<AdminLaunchRequestDto | null> {
    const list = await this.getLaunchRequestList(accountId);
    return list.adminRequests.find(r => r.launchqueueId === launchqueueId) ?? null;
  }

  /**
   * Get all requests filtered by status (e.g. 'pending', 'completed').
   */
  async getRequestsByStatus(
    accountId: string,
    status: string
  ): Promise<AdminLaunchRequestDto[]> {
    const list = await this.getLaunchRequestList(accountId);
    return list.adminRequests.filter(r => r.status === status);
  }
}
